import { useState } from 'react';
import {
  MississippiCoverReportTypeBox,
  MississippiCoverRole,
  MississippiCoverVariant,
  MississippiJudicialReportTypeBox,
  mississippiCoverCorrections,
  type MississippiCoverCorrectionsDto,
  type MississippiCoverReviewDto,
  type MississippiCoverValuesDto,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PendingButton } from '@/components/ui/pending-button';
import {
  useMississippiCoverReview,
  useSaveMississippiCoverCorrections,
} from '@/hooks/queries/useMississippiCoverReview';
import { useGenerateWholeReport } from '@/hooks/queries/useFinanceReports';
import { triggerBrowserDownload } from './downloadHelpers';
import { useCampaignStore } from '@/stores';

/**
 * The in-app review-and-correct surface for a report's filled Mississippi cover. The treasurer signs the
 * cover and is personally responsible for it, and the app pre-fills only what it knows (inferring the
 * report type and the filer type), so every cell is correctable here instead of in a desktop PDF editor.
 * Corrections are stored on the report; the download here — and the send — regenerate the cover with them.
 */

type TextKey =
  | 'nameOfFiler'
  | 'address'
  | 'cityZip'
  | 'telephone'
  | 'fax'
  | 'contactOrTreasurer'
  | 'email'
  | 'officeSought'
  | 'typedSignatureName'
  | 'signingDate';

type MoneyKey =
  | 'jan1CashOnHand'
  | 'contributionsItemized'
  | 'contributionsNonItemized'
  | 'contributionsThisPeriod'
  | 'contributionsYearToDate'
  | 'disbursementsItemized'
  | 'disbursementsNonItemized'
  | 'disbursementsThisPeriod'
  | 'disbursementsYearToDate'
  | 'endingCashOnHand';

type FieldKey = TextKey | MoneyKey;
type ChoiceKey = 'reportType' | 'informationDiffers' | 'role';

const IDENTITY_FIELDS: { key: TextKey; label: string }[] = [
  { key: 'nameOfFiler', label: 'Name of candidate or committee' },
  { key: 'address', label: 'Address' },
  { key: 'cityZip', label: 'City/Zip' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'fax', label: 'Fax' },
  { key: 'contactOrTreasurer', label: 'Contact / treasurer' },
  { key: 'email', label: 'Email address' },
];

const OFFICE_SOUGHT = { key: 'officeSought' as const, label: 'Office sought' };

const CERTIFICATION_FIELDS: { key: TextKey; label: string; type?: string }[] = [
  { key: 'typedSignatureName', label: 'Signer name' },
  { key: 'signingDate', label: 'Date signed', type: 'date' },
];

// The totals grid, in the form's own layout: the January-1 opening, the two footed rows, the close.
const TOTAL_ROWS: { title: string; fields: { key: MoneyKey; label: string }[] }[] = [
  { title: 'Cash on hand', fields: [{ key: 'jan1CashOnHand', label: 'January 1 cash on hand' }] },
  {
    title: 'Contributions',
    fields: [
      { key: 'contributionsItemized', label: 'Contributions itemized' },
      { key: 'contributionsNonItemized', label: 'Contributions non-itemized' },
      { key: 'contributionsThisPeriod', label: 'Contributions this period' },
      { key: 'contributionsYearToDate', label: 'Contributions calendar year-to-date' },
    ],
  },
  {
    title: 'Disbursements',
    fields: [
      { key: 'disbursementsItemized', label: 'Disbursements itemized' },
      { key: 'disbursementsNonItemized', label: 'Disbursements non-itemized' },
      { key: 'disbursementsThisPeriod', label: 'Disbursements this period' },
      { key: 'disbursementsYearToDate', label: 'Disbursements calendar year-to-date' },
    ],
  },
  { title: 'Closing', fields: [{ key: 'endingCashOnHand', label: 'Ending cash on hand' }] },
];

const MONEY_KEYS = new Set<FieldKey>(TOTAL_ROWS.flatMap((row) => row.fields.map((f) => f.key)));
const isMoneyKey = (key: FieldKey): key is MoneyKey => MONEY_KEYS.has(key);

// Records (not switches) so a new enum member without a label is a compile error.
const ROLE_LABELS: Record<MississippiCoverRole, string> = {
  [MississippiCoverRole.IndividualCandidate]: 'Individual candidate',
  [MississippiCoverRole.CandidateCommittee]: "Candidate's committee",
  [MississippiCoverRole.PoliticalCommittee]: 'Political committee',
};

const COUNTY_REPORT_TYPE_LABELS: Record<MississippiCoverReportTypeBox, string> = {
  [MississippiCoverReportTypeBox.PreElection]: 'Pre-Election Report',
  [MississippiCoverReportTypeBox.PreRunoff]: 'Pre-Runoff Report',
  [MississippiCoverReportTypeBox.Annual]: 'Annual Report',
  [MississippiCoverReportTypeBox.Termination]: 'Termination Report',
};

const JUDICIAL_REPORT_TYPE_LABELS: Record<MississippiJudicialReportTypeBox, string> = {
  [MississippiJudicialReportTypeBox.May8Periodic]: 'May 8 Periodic Report',
  [MississippiJudicialReportTypeBox.June10Periodic]: 'June 10 Periodic Report',
  [MississippiJudicialReportTypeBox.July10Periodic]: 'July 10 Periodic Report',
  [MississippiJudicialReportTypeBox.October9Periodic]: 'October 9 Periodic Report',
  [MississippiJudicialReportTypeBox.October27PreElection]: 'October 27 Pre-Election Report',
  [MississippiJudicialReportTypeBox.November24PreRunoff]: 'November 24 Pre-Runoff Report',
  [MississippiJudicialReportTypeBox.January9Periodic]: 'January 9 Periodic Report',
  [MississippiJudicialReportTypeBox.Termination]: 'Termination Report',
};

function reportTypeLabel(variant: MississippiCoverVariant, choice: string): string {
  const labels: Record<string, string> =
    variant === MississippiCoverVariant.Judicial ? JUDICIAL_REPORT_TYPE_LABELS : COUNTY_REPORT_TYPE_LABELS;
  return labels[choice] ?? choice;
}

function display(values: MississippiCoverValuesDto, key: FieldKey): string {
  const value = values[key];
  if (value === null || value === undefined) return '';
  return MONEY_KEYS.has(key) ? Number(value).toFixed(2) : String(value);
}

// Accept what a treasurer types for money ("1,234.50", "$42"); anything else is refused, never guessed.
function parseMoney(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  return /^-?\d+(\.\d{1,2})?$/.test(cleaned) ? Number(cleaned) : null;
}

export function MississippiCoverReviewPanel({ reportId }: { reportId: string }) {
  const { data, isLoading, error } = useMississippiCoverReview(reportId);
  // The version this sheet just saved — lifted out of the form because a save remounts it.
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading the cover…</p>;
  if (error || !data) {
    return (
      <p className="text-sm text-destructive" data-testid="ms-cover-load-error">
        {error instanceof Error ? error.message : 'The cover could not be loaded.'}
      </p>
    );
  }
  // Remount on each new server version so a save (or a concurrent change) re-seeds the form.
  return (
    <CoverForm
      key={data.concurrencyVersion}
      review={data}
      justSaved={savedVersion === data.concurrencyVersion}
      onSaved={setSavedVersion}
    />
  );
}

function CoverForm({
  review,
  justSaved,
  onSaved,
}: {
  review: MississippiCoverReviewDto;
  justSaved: boolean;
  onSaved: (version: number) => void;
}) {
  const { saveCoverCorrections } = useSaveMississippiCoverCorrections(review.reportId);
  const { generateWholeReport } = useGenerateWholeReport();

  const [draft, setDraft] = useState<Record<FieldKey, string>>(() => {
    const initial = {} as Record<FieldKey, string>;
    for (const key of [...IDENTITY_FIELDS, OFFICE_SOUGHT, ...CERTIFICATION_FIELDS].map((f) => f.key)) {
      initial[key] = display(review.effective, key);
    }
    for (const key of MONEY_KEYS) initial[key] = display(review.effective, key);
    return initial;
  });
  const [reportType, setReportType] = useState<string | null>(review.effective.reportType);
  const [informationDiffers, setInformationDiffers] = useState(review.effective.informationDiffers);
  const [role, setRole] = useState<MississippiCoverRole>(review.role);
  // Which cells carry a correction — seeded from the stored set, so an untouched stored correction survives
  // a save and an untouched derived cell keeps tracking the ledger.
  const [corrected, setCorrected] = useState<Set<FieldKey | ChoiceKey>>(() => {
    const keys = new Set<FieldKey | ChoiceKey>();
    for (const [key, value] of Object.entries(review.corrections)) {
      if (value !== null && value !== undefined) keys.add(key as FieldKey | ChoiceKey);
    }
    return keys;
  });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<null | 'save' | 'download'>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(justSaved);

  // review.editable reflects the report's status only; saving also needs Finance Manage.
  const canManage = useCampaignStore((st) => st.canManage('Finance'));
  const readOnly = !review.editable || !canManage;

  const markCorrected = (key: FieldKey | ChoiceKey) => {
    setCorrected((prev) => new Set(prev).add(key));
    setDirty(true);
    setSavedNote(false);
  };

  const editField = (key: FieldKey, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    markCorrected(key);
  };

  const resetField = (key: FieldKey) => {
    setDraft((prev) => ({ ...prev, [key]: display(review.derived, key) }));
    setCorrected((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setDirty(true);
    setSavedNote(false);
  };

  const buildCorrections = (): MississippiCoverCorrectionsDto | string => {
    const corrections = mississippiCoverCorrections();
    const labelOf = (key: FieldKey) =>
      [...IDENTITY_FIELDS, OFFICE_SOUGHT, ...CERTIFICATION_FIELDS, ...TOTAL_ROWS.flatMap((r) => r.fields)].find(
        (f) => f.key === key,
      )?.label ?? key;

    for (const key of Object.keys(draft) as FieldKey[]) {
      if (!corrected.has(key)) continue;
      const raw = draft[key];
      if (isMoneyKey(key)) {
        const amount = parseMoney(raw);
        if (amount === null) return `${labelOf(key)} must be a dollar amount, like 1,234.50.`;
        corrections[key] = amount;
      } else if (key === 'signingDate') {
        if (raw) corrections[key] = raw;
      } else {
        corrections[key] = raw;
      }
    }
    // Choosing the derived box again is no override — it keeps following the report's own type.
    if (corrected.has('reportType') && reportType && reportType !== review.derived.reportType) {
      corrections.reportType = reportType;
    }
    if (corrected.has('informationDiffers') && informationDiffers) corrections.informationDiffers = true;
    if (corrected.has('role') && role !== review.derivedRole) corrections.role = role;
    return corrections;
  };

  const handleSave = async () => {
    setErrorMessage(null);
    const corrections = buildCorrections();
    if (typeof corrections === 'string') {
      setErrorMessage(corrections);
      return;
    }
    setBusy('save');
    try {
      const saved = await saveCoverCorrections({
        reportId: review.reportId,
        data: { corrections, concurrencyVersion: review.concurrencyVersion },
      });
      onSaved(saved.concurrencyVersion);
      setDirty(false);
      setSavedNote(true);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'The corrections could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    setErrorMessage(null);
    setBusy('download');
    try {
      // The generate applies the stored corrections; its first file is the cover.
      const { fileBlob, fileName } = await generateWholeReport(review.reportId);
      triggerBrowserDownload(fileBlob, fileName);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'The cover could not be downloaded.');
    } finally {
      setBusy(null);
    }
  };

  const renderField = (key: FieldKey, label: string, type = 'text') => {
    const id = `ms-cover-${key}`;
    const isCorrected = corrected.has(key);
    const derivedValue = display(review.derived, key);
    return (
      <div key={key} className="space-y-1">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <Input
          id={id}
          type={type}
          inputMode={MONEY_KEYS.has(key) ? 'decimal' : undefined}
          value={draft[key]}
          disabled={readOnly || busy !== null}
          onChange={(e) => editField(key, e.target.value)}
          className={MONEY_KEYS.has(key) ? 'text-right tabular-nums' : undefined}
        />
        {isCorrected && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span data-testid={`ms-cover-derived-${key}`}>Pre-filled: {derivedValue || '(blank)'}</span>
            {!readOnly && (
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                aria-label={`Reset ${label}`}
                onClick={() => resetField(key)}
              >
                Reset
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const roleChanged = role !== review.role;

  return (
    <div className="space-y-6" data-testid="ms-cover-review">
      <p className="text-sm text-muted-foreground">
        These are the values the app filled in on the official cover. You sign this cover, so check each one
        and correct anything that's wrong. Corrections are saved with this report and used every time the
        cover is generated — for download and when it's sent.
      </p>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Filer</h3>
        <div className="space-y-1">
          <label htmlFor="ms-cover-role" className="text-sm font-medium">
            Filer type
          </label>
          <select
            id="ms-cover-role"
            value={role}
            disabled={readOnly || busy !== null}
            onChange={(e) => {
              setRole(e.target.value as MississippiCoverRole);
              markCorrected('role');
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Object.values(MississippiCoverRole).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Picks which official form files and who signs it. Worked out from your committee setup as{' '}
            {ROLE_LABELS[review.derivedRole]}.
            {roleChanged && ' Save to see the name and signer for the new form.'}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {IDENTITY_FIELDS.map((f) => renderField(f.key, f.label))}
          {review.hasOfficeSought && renderField(OFFICE_SOUGHT.key, OFFICE_SOUGHT.label)}
        </div>
        {review.hasInformationDiffersBox && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={informationDiffers}
              disabled={readOnly || busy !== null}
              onChange={(e) => {
                setInformationDiffers(e.target.checked);
                markCorrected('informationDiffers');
              }}
            />
            The information above is different from the previous report
          </label>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold" id="ms-cover-report-type-heading">
          Type of report
        </h3>
        <div role="radiogroup" aria-label="Type of report" className="space-y-1">
          {review.reportTypeChoices.map((choice) => {
            const id = `ms-cover-report-type-${choice}`;
            return (
              <div key={choice} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={id}
                  name="ms-cover-report-type"
                  value={choice}
                  checked={reportType === choice}
                  disabled={readOnly || busy !== null}
                  onChange={() => {
                    setReportType(choice);
                    markCorrected('reportType');
                  }}
                />
                <label htmlFor={id} className="text-sm">
                  {reportTypeLabel(review.variant, choice)}
                </label>
              </div>
            );
          })}
        </div>
        {corrected.has('reportType') && !readOnly && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span data-testid="ms-cover-derived-reportType">
              Pre-filled:{' '}
              {review.derived.reportType ? reportTypeLabel(review.variant, review.derived.reportType) : '(none marked)'}
            </span>
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground"
              aria-label="Reset Type of report"
              onClick={() => {
                setReportType(review.derived.reportType);
                setCorrected((prev) => {
                  const next = new Set(prev);
                  next.delete('reportType');
                  return next;
                });
                setDirty(true);
                setSavedNote(false);
              }}
            >
              Reset
            </button>
          </div>
        )}
        {review.derived.reportType === null && !corrected.has('reportType') && (
          <p className="text-xs text-muted-foreground">
            The app couldn't tell which box this report is, so none is marked. Choose one.
          </p>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Reported contributions and disbursements</h3>
        {TOTAL_ROWS.map((row) => (
          <div key={row.title} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {row.fields.map((f) => renderField(f.key, f.label))}
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          In-kind contributions are listed on their own schedule and are not added into these totals.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Certification</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {CERTIFICATION_FIELDS.map((f) => renderField(f.key, f.label, f.type))}
        </div>
        <p className="text-xs text-muted-foreground">
          The cover carries a typed signature, never a digital certificate.
        </p>
      </section>

      {errorMessage && (
        <p className="text-sm text-destructive" data-testid="ms-cover-error">
          {errorMessage}
        </p>
      )}
      {savedNote && !dirty && (
        <p className="text-sm text-muted-foreground" data-testid="ms-cover-saved">
          Saved. Download the cover to check it, or send the report as usual.
        </p>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <PendingButton type="button" pending={busy === 'save'} disabled={busy !== null} onClick={handleSave}>
            Save corrections
          </PendingButton>
          <Button
            type="button"
            variant="outline"
            disabled={dirty || busy !== null}
            onClick={handleDownload}
            title={dirty ? 'Save your corrections first' : undefined}
          >
            Download the filing
          </Button>
        </div>
      )}
    </div>
  );
}
